import { FC, useId, useEffect, useRef } from "react";
import { Button, IconButton, Modal, SvgIcon } from "@mui/material";

import "./style.scss";
import { ReactComponent as CloseIcon } from "../../assets/cross.icon.svg";

export enum ConfirmationPopupType {
    default,
    dangerous
}

const ConfirmationPopup = ({
    open,
    type = ConfirmationPopupType.default,
    icon,
    title,
    subtitle,
    description,
    actionName,
    showCancelButton = false,
    handleAction,
    handleClose
}: {
    open: boolean;
    type?: ConfirmationPopupType;
    icon?: FC<React.SVGProps<SVGSVGElement>>;
    title: string;
    subtitle?: string;
    description: string;
    actionName: string;
    showCancelButton?: boolean;
    handleAction: () => void;
    handleClose: () => void;
}) => {
    const titleId = useId();
    const descriptionId = useId();

    // Confirmation dialogs (esp. destructive ones) must tell screen-reader users
    // WHAT they're confirming the moment the dialog opens. VoiceOver announces a
    // focused dialog's aria-label but not aria-describedby inline, so fold
    // title + subtitle + description into the accessible name and drop
    // aria-describedby (so readers that DO announce describedby don't repeat it).
    const dialogLabel = [title, subtitle, description].filter(Boolean).join(". ");

    // Restore focus to the control that opened the popup on close (WCAG 2.4.3);
    // MUI Modal's built-in restore does not fire reliably for these dialogs, so
    // capture the opener during render (before the Modal mounts and takes focus)
    // and return focus to it after close, deferred past teardown via rAF.
    const triggerRef = useRef<HTMLElement | null>(null);
    const wasOpenRef = useRef(false);
    if (open && !wasOpenRef.current) {
        const opener = document.activeElement as HTMLElement | null;
        // When opened from a menu, the active element is a menu item that
        // unmounts with the menu; resolve to the menu's trigger (via the list's
        // aria-labelledby) so focus returns to the kebab/button, not <body>.
        const menuList = opener?.closest('[role="menu"]');
        const labelledBy = menuList?.getAttribute("aria-labelledby");
        const trigger = labelledBy ? document.getElementById(labelledBy) : null;
        triggerRef.current = trigger ?? opener;
    }
    wasOpenRef.current = open;

    useEffect(() => {
        if (!open && triggerRef.current) {
            const trigger = triggerRef.current;
            triggerRef.current = null;
            requestAnimationFrame(() => {
                if (trigger.isConnected) {
                    trigger.focus();
                }
            });
        }
    }, [open]);

    return (
        <Modal
            style={{ display: "flex" }}
            className="confirmation-area"
            open={open}
            onClose={handleClose}
        >
            <div
                className={`confirmation-window${type === ConfirmationPopupType.dangerous ? " confirmation-window--dangerous" : ""}`}
                role="dialog"
                aria-modal="true"
                // aria-label (not aria-labelledby): VoiceOver announces a focused
                // dialog's aria-label reliably, but not an aria-labelledby-derived
                // name on the same programmatic focus. dialogLabel folds in the
                // description (see above) so the full prompt is spoken on open.
                aria-label={dialogLabel}
            >
                <IconButton
                    className="confirmation-window__button-close"
                    aria-label="Close"
                    onClick={handleClose}
                >
                    <CloseIcon />
                </IconButton>
                <div className="confirmation-window-title">
                    {icon && (
                        <SvgIcon
                            className="confirmation-window-title__icon"
                            component={icon}
                            inheritViewBox
                        />
                    )}
                    <span
                        id={titleId}
                        className="confirmation-window-title__text"
                        style={{ marginLeft: icon ? 16 : 0 }}
                    >
                        {title}
                    </span>
                </div>
                {subtitle && <div className="confirmation-window-subtitle">{subtitle}</div>}
                <div id={descriptionId} className="confirmation-window-description">
                    {description}
                </div>
                <div className="confirmation-window-actions">
                    {showCancelButton && (
                        <Button className="secondary-button" variant="text" onClick={handleClose}>
                            Cancel
                        </Button>
                    )}
                    <Button
                        className={`primary-button${showCancelButton ? " confirmation-window-actions-button-margin" : ""}`}
                        variant="text"
                        onClick={handleAction}
                    >
                        {actionName}
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export default ConfirmationPopup;
